@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginekartei

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import org.example.project.utils.All
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.css.*
import org.w3c.files.File


const val KARTEI_DECKBLATT_ID = "__deck__"
private const val DEFAULT_AREA_KEY = "__default__"
private val KARTEI_IMAGE_FIELDS = listOf("screen1", "screen2", "screen3", "screen4", "screen5")
private val karteiJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = true
}

object EngineKarteiFieldType {
    const val TEXT = "text"
    const val CHECKBOX = "checkbox"
    const val DROPDOWN = "dropdown"
}

typealias EngineKarteiField = All

fun EngineKarteiField(
    id: String,
    header: String,
    type: String = EngineKarteiFieldType.TEXT,
    editable: Boolean = true,
    options: List<String> = emptyList(),
    area: String = "",
    placeholder: String = ""
): All = All(
    id = id,
    label = header,
    typ = type,
    aktiv = editable,
    columns = options,
    bereich = area,
    platzcode = placeholder
)

fun EngineKarteiField.copy(
    id: String = this.id,
    header: String = this.label,
    type: String = this.typ.ifBlank { EngineKarteiFieldType.TEXT },
    editable: Boolean = this.aktiv,
    options: List<String> = this.columns,
    area: String = this.bereich,
    placeholder: String = this.platzcode
): All = EngineKarteiField(
    id = id,
    header = header,
    type = type,
    editable = editable,
    options = options,
    area = area,
    placeholder = placeholder
)

private val EngineKarteiField.header: String
    get() = label

private val EngineKarteiField.type: String
    get() = typ.ifBlank { EngineKarteiFieldType.TEXT }

private val EngineKarteiField.editable: Boolean
    get() = aktiv

private val EngineKarteiField.options: List<String>
    get() = columns

private val EngineKarteiField.area: String
    get() = bereich

private val EngineKarteiField.placeholder: String
    get() = platzcode

/**
 * Optionale Seiten-Aktion für eine bestimmte Page-Nummer (0-basiert).
 * Wenn eine Aktion für die aktive Seite registriert ist, ersetzt sie den normalen
 * "Speichern"-Flow. Der Button zeigt [label] statt "Speichern".
 * [action] erhält den aktuellen [All]-Zustand und gibt den gespeicherten Zustand zurück.
 */
class EngineKarteiPageAction(
    val label: String,
    val action: suspend (state: All) -> All
)

/**
 * Optionaler Renderer-Override für ein einzelnes Feld (nach [id]).
 * Wird statt des Standard-Inputs gerendert. Erhält den aktuellen Wert,
 * einen Single-Value-Setter und einen Multi-Value-Setter (z. B. für Firmen-Suche).
 */
class KarteiFieldOverride(
    val compose: @Composable (
        value: String,
        onValue: (String) -> Unit,
        onMultiValue: (Map<String, String>) -> Unit
    ) -> Unit
)

class KarteiPageOverride(
    val compose: @Composable (
        values: Map<String, String>,
        onValue: (String, String) -> Unit,
        onMultiValue: (Map<String, String>) -> Unit
    ) -> Unit
)

private typealias EngineKarteiPageState = All
private typealias EngineKarteiFieldState = All
private typealias EngineKarteiImageState = All

private fun EngineKarteiPageState(
    id: String,
    label: String,
    kind: String,
    fields: List<EngineKarteiField>
): All = All(
    id = id,
    bezeichnung = label,
    art = kind,
    module = fields
)

private val EngineKarteiPageState.pageLabel: String
    get() = bezeichnung

private val EngineKarteiPageState.kind: String
    get() = art

private val EngineKarteiPageState.fields: List<EngineKarteiField>
    get() = module

private fun karteiFieldState(
    textValues: Map<String, String>,
    checkValues: Map<String, Boolean>
): All = All(
    karteiSeiten = textValues,
    screenBerechtigungen = checkValues.mapValues { it.value.toString() }
)

private val EngineKarteiFieldState.textValues: Map<String, String>
    get() = karteiSeiten

private val EngineKarteiFieldState.checkValues: Map<String, Boolean>
    get() = screenBerechtigungen.mapValues { (_, value) -> value == "true" }

private fun karteiImageState(imageValues: Map<String, String>): All = All(imageValues = imageValues)

private fun All.toJsonObject(): JsonObject =
    karteiJson.encodeToJsonElement(All.AllNames.serializer(), this).jsonObject

private fun JsonElement.asFieldString(): String = when (this) {
    is JsonPrimitive -> contentOrNull ?: content
    else -> toString()
}

private fun JsonObject.readString(field: String): String =
    this[field]?.asFieldString().orEmpty()

private fun JsonObject.readBoolean(field: String): Boolean {
    val element = this[field] as? JsonPrimitive ?: return false
    return element.booleanOrNull ?: when (element.content.lowercase()) {
        "true", "1", "yes", "ja" -> true
        else -> false
    }
}

private fun extractFieldState(
    state: All,
    fields: List<EngineKarteiField>
): EngineKarteiFieldState {
    val json = state.toJsonObject()
    val textValues = linkedMapOf<String, String>()
    val checkValues = linkedMapOf<String, Boolean>()

    fields.forEach { field ->
        val fieldId = field.id
        if (fieldId.isBlank()) return@forEach

        if (field.type.lowercase() == EngineKarteiFieldType.CHECKBOX) {
            checkValues[fieldId] = json.readBoolean(fieldId)
        } else {
            textValues[fieldId] = json.readString(fieldId)
        }
    }

    return karteiFieldState(
        textValues = textValues,
        checkValues = checkValues
    )
}

private fun extractImageState(
    state: All,
    pages: List<EngineKarteiPageState>,
    imageFallbackSrc: String
): EngineKarteiImageState {
    val json = state.toJsonObject()
    return karteiImageState(
        imageValues = pages.associate { page ->
            val pageIndex = pages.indexOf(page)
            val fieldId = resolvePageImageField(pageIndex)
            page.id to json.readString(fieldId).ifBlank { imageFallbackSrc }
        }
    )
}

private fun materializeState(
    base: All,
    textValues: Map<String, String>,
    checkValues: Map<String, Boolean>,
    imageValues: Map<String, String>
): All {
    val next = base.toJsonObject().toMutableMap()

    textValues.forEach { (fieldId, value) ->
        next[fieldId] = JsonPrimitive(value)
    }
    checkValues.forEach { (fieldId, value) ->
        next[fieldId] = JsonPrimitive(value)
    }
    imageValues.forEach { (fieldId, value) ->
        next[fieldId] = JsonPrimitive(value)
    }

    return karteiJson.decodeFromJsonElement(
        All.AllNames.serializer(),
        JsonObject(next)
    )
}

private fun createNewState(idValue: String = "neu"): All {
    val base = All(id = idValue, personalnummer = "", aktiv = false)
    return base
}

private fun debugMeinProfilNetzwerk(event: String, payload: Any?) {
    js("console.log")("[MeinProfilScreen][Netzwerk]", event, payload)
}

private fun normalizeState(loaded: All, requestedId: String): All {
    val resolvedId = loaded.id.ifBlank {
        requestedId
    }
    val normalized = loaded.copy(
        id = resolvedId
    )
    return normalized
}

private fun resolveAreaKey(field: EngineKarteiField): String =
    field.area.ifBlank { DEFAULT_AREA_KEY }

private fun resolveAreaTitle(field: EngineKarteiField): String =
    field.area

private fun normalizeFields(fields: List<EngineKarteiField>): List<EngineKarteiField> =
    fields.filter { it.id.isNotBlank() }

private fun groupFields(fields: List<EngineKarteiField>, visibleAreas: Set<String>): List<EngineKarteiFieldGroup> {
    if (fields.isEmpty()) return emptyList()

    val filtered = fields.filter { field ->
        visibleAreas.isEmpty() || visibleAreas.contains(resolveAreaKey(field))
    }
    if (filtered.isEmpty()) return emptyList()

    return filtered
        .groupBy(::resolveAreaKey)
        .map { (key, groupFields) ->
            EngineKarteiFieldGroup(
                key = key,
                title = resolveAreaTitle(groupFields.first()),
                fields = groupFields
            )
        }
}

private fun recordSummary(all: All): String {
    val primary = listOf(all.name, all.bezeichnung, all.userName).firstOrNull { it.isNotBlank() }.orEmpty()
    val secondary = listOf(all.vorname, all.nachname).filter { it.isNotBlank() }.joinToString(" ")
    return listOf(primary, secondary).firstOrNull { it.isNotBlank() }.orEmpty()
}

private fun resolvePageImageField(pageIndex: Int): String =
    KARTEI_IMAGE_FIELDS.getOrElse(pageIndex.coerceAtLeast(0)) { KARTEI_IMAGE_FIELDS.last() }

private data class EngineKarteiPageStore(
    val committedState: All? = null,
    val workingState: All? = null,
    val textValues: Map<String, String> = emptyMap(),
    val checkValues: Map<String, Boolean> = emptyMap(),
    val imageValue: String = "",
    val dirty: Boolean = false
)

@Composable
fun <E> EngineKarteiScreen(
    entities: List<E>,
    screenTitle: String = "",
    entityId: (E) -> String,
    entityLabel: (E) -> String,
    loadState: suspend (userId: String) -> All,
    saveState: suspend (state: All) -> All,
    anchorFields: List<EngineKarteiField>,
    page1Label: String,
    page1Fields: List<EngineKarteiField>,
    page2Label: String = "",
    page2Fields: List<EngineKarteiField> = emptyList(),
    page3Label: String = "",
    page3Fields: List<EngineKarteiField> = emptyList(),
    page4Label: String = "",
    page4Fields: List<EngineKarteiField> = emptyList(),
    page5Label: String = "",
    page5Fields: List<EngineKarteiField> = emptyList(),
    page6Label: String = "",
    page6Fields: List<EngineKarteiField> = emptyList(),
    loadGalleryImages: suspend (entityId: String) -> List<All> = { emptyList() },
    uploadGalleryImage: suspend (entityId: String, file: File) -> All? = { _, _ -> null },
    deleteGalleryImage: suspend (entityId: String, image: All) -> Boolean = { _, _ -> false },

    imageFallbackSrc: String = "images/add.svg",
    staticPhotoSrc: String? = null,
    showPhoto: Boolean = true,
    showSave: Boolean = true,
    canCreate: Boolean = true,
    createState: (() -> All)? = null,
    createPageId: String = "",
    visibleAreas: Set<String> = emptySet(),
    pageActions: Map<Int, EngineKarteiPageAction> = emptyMap(),
    secondaryPageActions: Map<Int, EngineKarteiPageAction> = emptyMap(),
    fieldOverrides: Map<String, KarteiFieldOverride> = emptyMap(),
    pageOverrides: Map<String, KarteiPageOverride> = emptyMap(),
    screenKey: String = "",
    pageScopedState: Boolean = false,
    pageAnchorFields: Map<String, List<EngineKarteiField>> = emptyMap(),
    loadPageState: (suspend (entityId: String, pageId: String) -> All?)? = null,
    savePageState: (suspend (entityId: String, pageId: String, state: All) -> All?)? = null,
    pageStateResetKey: String = "",
    anchorDeckblatt: Boolean = false,
    loadAnchorState: (suspend () -> All?)? = null,
    saveAnchorState: (suspend (All) -> All?)? = null,
    selectorWidthPx: Int = 320,
    showCreate: Boolean = true,
    createDialog: (@Composable (onCreate: (All) -> Unit, onCreateBlank: () -> Unit, onClose: () -> Unit) -> Unit)? = null,
    selectorContent: (@Composable (activePageId: String) -> Unit)? = null,
    actionContent: (@Composable (activePageId: String) -> Unit)? = null
) {
    val scope = rememberCoroutineScope()

    val normalizedPages = remember(
        page1Label, page1Fields,
        page2Label, page2Fields,
        page3Label, page3Fields,
        page4Label, page4Fields,
        page5Label, page5Fields,
        page6Label, page6Fields
    ) {
        listOf(
            Triple(page1Label, page1Fields, 0),
            Triple(page2Label, page2Fields, 1),
            Triple(page3Label, page3Fields, 2),
            Triple(page4Label, page4Fields, 3),
            Triple(page5Label, page5Fields, 4),
            Triple(page6Label, page6Fields, 5)
        ).mapNotNull { (label, fields, index) ->
            val cleanLabel = label
            if (cleanLabel.isBlank()) {
                null
            } else {
                EngineKarteiPageState(
                    id = "page_${index + 1}",
                    label = cleanLabel,
                    kind = "data",
                    fields = normalizeFields(fields)
                )
            }
        }
    }
    val normalizedAnchorFields = remember(anchorFields) { normalizeFields(anchorFields) }
    val normalizedPageAnchorFields = remember(pageAnchorFields) {
        pageAnchorFields.mapValues { (_, fields) -> normalizeFields(fields) }
    }
    val allFields = remember(normalizedAnchorFields, normalizedPages) {
        (normalizedAnchorFields + normalizedPages.flatMap { it.fields }).distinctBy { it.id }
    }
    val normalizedEntityIds = remember(entities) { entities.map(entityId).filter { it.isNotBlank() } }

    var activePageId by remember(normalizedPages) {
        mutableStateOf(normalizedPages.firstOrNull()?.id.orEmpty())
    }
    var selectedEntityId by remember { mutableStateOf(normalizedEntityIds.firstOrNull().orEmpty()) }

    var committedState by remember { mutableStateOf<All?>(null) }
    var workingState by remember { mutableStateOf<All?>(null) }
    var pageStores by remember { mutableStateOf<Map<String, EngineKarteiPageStore>>(emptyMap()) }
    var loading by remember { mutableStateOf(false) }
    var saving by remember { mutableStateOf(false) }
    var imageDrafts by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var textValues by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var checkValues by remember { mutableStateOf<Map<String, Boolean>>(emptyMap()) }
    var isDirty by remember { mutableStateOf(false) }
    var imageEditorVisible by remember { mutableStateOf(false) }
    var anchorCommittedState by remember { mutableStateOf<All?>(null) }
    var createDialogOpen by remember { mutableStateOf(false) }

    fun fieldsForPage(pageId: String): List<EngineKarteiField> =
        normalizedPageAnchorFields[pageId].orEmpty() +
            (normalizedPages.firstOrNull { it.id == pageId }?.fields.orEmpty())

    fun pageStore(pageId: String): EngineKarteiPageStore = pageStores[pageId] ?: EngineKarteiPageStore(
        imageValue = imageDrafts[pageId].orEmpty()
    )

    fun applyPageStore(pageId: String, store: EngineKarteiPageStore) {
        pageStores = pageStores + (pageId to store)
        imageDrafts = imageDrafts + (pageId to store.imageValue.ifBlank { imageFallbackSrc })
    }

    fun mergeAnchorFieldState(state: All?) {
        if (!anchorDeckblatt) return
        val anchorFields = normalizedPages.firstOrNull()?.fields.orEmpty()
        if (anchorFields.isEmpty()) return
        val source = state ?: return
        val extracted = extractFieldState(source, anchorFields)
        textValues = textValues + extracted.textValues
        checkValues = checkValues + extracted.checkValues
    }

    fun pageImageValues(): Map<String, String> = normalizedPages.mapIndexed { index, page ->
        resolvePageImageField(index) to imageDrafts[page.id].orEmpty().ifBlank { imageFallbackSrc }
    }.toMap()

    fun resetFieldState(
        state: All,
        fields: List<EngineKarteiField> = allFields,
        pages: List<EngineKarteiPageState> = normalizedPages,
        replaceAll: Boolean = true
    ) {
        val extracted = extractFieldState(state, fields)
        textValues = if (replaceAll) {
            extracted.textValues
        } else {
            textValues + extracted.textValues
        }
        checkValues = if (replaceAll) {
            extracted.checkValues
        } else {
            checkValues + extracted.checkValues
        }
        val images = extractImageState(state, pages, imageFallbackSrc).imageValues
        imageDrafts = if (replaceAll) {
            images
        } else {
            imageDrafts + images
        }
    }

    fun rebuildWorkingState(base: All): All = materializeState(
        base = base,
        textValues = textValues,
        checkValues = checkValues,
        imageValues = pageImageValues()
    )

    fun rebuildPageWorkingState(pageId: String, base: All, store: EngineKarteiPageStore): All = materializeState(
        base = base,
        textValues = store.textValues,
        checkValues = store.checkValues,
        imageValues = mapOf(resolvePageImageField(normalizedPages.indexOfFirst { it.id == pageId }.coerceAtLeast(0)) to store.imageValue.ifBlank { imageFallbackSrc })
    )

    fun setTextValue(fieldId: String, value: String) {
        if (pageScopedState) {
            val pageId = activePageId
            if (pageId.isBlank()) return
            val currentStore = pageStore(pageId)
            val current = currentStore.workingState ?: return
            val nextTextValues = currentStore.textValues + (fieldId to value)
            val nextStore = currentStore.copy(
                workingState = rebuildPageWorkingState(
                    pageId = pageId,
                    base = currentStore.committedState ?: current,
                    store = currentStore.copy(textValues = nextTextValues)
                ).copy(aktiv = false),
                textValues = nextTextValues,
                dirty = true
            )
            applyPageStore(pageId, nextStore)
            return
        }
        val current = workingState ?: return
        val nextTextValues = textValues + (fieldId to value)
        textValues = nextTextValues
        workingState = materializeState(
            base = committedState ?: current,
            textValues = nextTextValues,
            checkValues = checkValues,
            imageValues = pageImageValues()
        ).copy(aktiv = false)
        isDirty = true
    }

    fun setMultipleTextValues(values: Map<String, String>) {
        if (pageScopedState) {
            val pageId = activePageId
            if (pageId.isBlank()) return
            val currentStore = pageStore(pageId)
            val current = currentStore.workingState ?: return
            val nextTextValues = currentStore.textValues + values
            val nextStore = currentStore.copy(
                workingState = rebuildPageWorkingState(
                    pageId = pageId,
                    base = currentStore.committedState ?: current,
                    store = currentStore.copy(textValues = nextTextValues)
                ).copy(aktiv = false),
                textValues = nextTextValues,
                dirty = true
            )
            applyPageStore(pageId, nextStore)
            return
        }
        val current = workingState ?: return
        val nextTextValues = textValues + values
        textValues = nextTextValues
        workingState = materializeState(
            base = committedState ?: current,
            textValues = nextTextValues,
            checkValues = checkValues,
            imageValues = pageImageValues()
        ).copy(aktiv = false)
        isDirty = true
    }

    fun setCheckValue(fieldId: String, value: Boolean) {
        if (pageScopedState) {
            val pageId = activePageId
            if (pageId.isBlank()) return
            val currentStore = pageStore(pageId)
            val current = currentStore.workingState ?: return
            val nextCheckValues = currentStore.checkValues + (fieldId to value)
            if (screenKey == "MeinProfilScreen" && pageId == "page_3") {
                debugMeinProfilNetzwerk(
                    "checkbox-change",
                    mapOf(
                        "fieldId" to fieldId,
                        "value" to value.toString(),
                        "selectedEntityId" to selectedEntityId,
                        "currentChecks" to currentStore.checkValues.mapValues { it.value.toString() },
                        "nextChecks" to nextCheckValues.mapValues { it.value.toString() }
                    )
                )
            }
            val nextStore = currentStore.copy(
                workingState = rebuildPageWorkingState(
                    pageId = pageId,
                    base = currentStore.committedState ?: current,
                    store = currentStore.copy(checkValues = nextCheckValues)
                ).copy(aktiv = false),
                checkValues = nextCheckValues,
                dirty = true
            )
            applyPageStore(pageId, nextStore)
            return
        }
        val current = workingState ?: return
        val nextCheckValues = checkValues + (fieldId to value)
        checkValues = nextCheckValues
        workingState = materializeState(
            base = committedState ?: current,
            textValues = textValues,
            checkValues = nextCheckValues,
            imageValues = pageImageValues()
        ).copy(aktiv = false)
        isDirty = true
    }

    fun setImageValue(pageId: String, imageField: String, value: String) {
        if (pageScopedState) {
            val currentStore = pageStore(pageId)
            val current = currentStore.workingState ?: return
            val nextStore = currentStore.copy(
                workingState = rebuildPageWorkingState(
                    pageId = pageId,
                    base = currentStore.committedState ?: current,
                    store = currentStore.copy(imageValue = value)
                ).copy(aktiv = false),
                imageValue = value,
                dirty = true
            )
            applyPageStore(pageId, nextStore)
            return
        }
        val current = workingState ?: return
        val nextImages = imageDrafts + (pageId to value)
        imageDrafts = nextImages
        workingState = materializeState(
            base = committedState ?: current,
            textValues = textValues,
            checkValues = checkValues,
            imageValues = normalizedPages.mapIndexed { index, page ->
                resolvePageImageField(index) to nextImages[page.id].orEmpty().ifBlank { imageFallbackSrc }
            }.toMap()
        ).copy(aktiv = false)
        isDirty = true
    }

    val selectedEntityIndex = normalizedEntityIds.indexOf(selectedEntityId).coerceAtLeast(0)
    val hasPreviousEntity = normalizedEntityIds.isNotEmpty() && selectedEntityIndex > 0
    val hasNextEntity = normalizedEntityIds.isNotEmpty() && selectedEntityIndex < normalizedEntityIds.lastIndex

    // The selector list is metadata only. It may refresh labels/ids, but it must not
    // silently replace an already loaded detail state while the user is working.
    LaunchedEffect(anchorDeckblatt, screenKey, normalizedEntityIds) {
        if (!anchorDeckblatt) {
            anchorCommittedState = null
            return@LaunchedEffect
        }

        val loadedAnchor = loadAnchorState?.invoke()
        anchorCommittedState = when {
            loadedAnchor != null -> normalizeState(loadedAnchor, KARTEI_DECKBLATT_ID)
            normalizedEntityIds.isEmpty() -> null
            else -> runCatching {
                normalizeState(loadState(normalizedEntityIds.first()), normalizedEntityIds.first())
            }.getOrNull()
        }

        if (normalizedPages.firstOrNull()?.id == activePageId) {
            mergeAnchorFieldState(anchorCommittedState)
        }
    }

    LaunchedEffect(normalizedEntityIds) {
        if (selectedEntityId.isBlank()) {
            selectedEntityId = normalizedEntityIds.firstOrNull().orEmpty()
        }
    }

    LaunchedEffect(selectedEntityId) {
        if (selectedEntityId.isBlank()) {
            committedState = null
            workingState = null
            pageStores = emptyMap()
            imageDrafts = emptyMap()
            textValues = emptyMap()
            checkValues = emptyMap()
            isDirty = false
            return@LaunchedEffect
        }

        if (selectedEntityId == "neu") {
            if (workingState == null || workingState?.id == "neu") {
                val newState = workingState ?: createNewState()
                committedState = null
                workingState = newState
                pageStores = emptyMap()
                resetFieldState(newState)
                mergeAnchorFieldState(anchorCommittedState)
                isDirty = true
            }
            return@LaunchedEffect
        }

        loading = true
        try {
            if (pageScopedState) {
                committedState = null
                workingState = null
                pageStores = emptyMap()
                imageDrafts = emptyMap()
                textValues = emptyMap()
                checkValues = emptyMap()
                isDirty = false
            } else {
                val loaded = normalizeState(loadState(selectedEntityId), selectedEntityId)
                committedState = loaded
                workingState = loaded
                resetFieldState(loaded)
                mergeAnchorFieldState(anchorCommittedState)
                isDirty = false
            }
        } finally {
            loading = false
        }
    }

    LaunchedEffect(pageScopedState, pageStateResetKey) {
        if (!pageScopedState) return@LaunchedEffect
        pageStores = emptyMap()
        imageDrafts = imageDrafts.filterKeys { false }
    }

    LaunchedEffect(selectedEntityId, activePageId, pageScopedState, pageStateResetKey) {
        if (!pageScopedState) return@LaunchedEffect
        val pageId = activePageId
        if (selectedEntityId.isBlank() || pageId.isBlank() || selectedEntityId == "neu") return@LaunchedEffect
        if (pageStores[pageId]?.workingState != null) return@LaunchedEffect

        loading = true
        try {
            val loaded = normalizeState(
                loadPageState?.invoke(selectedEntityId, pageId)
                    ?: loadState(selectedEntityId),
                selectedEntityId
            )
            val pageFields = fieldsForPage(pageId)
            val extracted = extractFieldState(loaded, pageFields)
            val pageIndex = normalizedPages.indexOfFirst { it.id == pageId }.coerceAtLeast(0)
            val imageValue = loaded.toJsonObject().readString(resolvePageImageField(pageIndex)).ifBlank { imageFallbackSrc }
            applyPageStore(
                pageId,
                EngineKarteiPageStore(
                    committedState = loaded,
                    workingState = loaded,
                    textValues = extracted.textValues,
                    checkValues = extracted.checkValues,
                    imageValue = imageValue,
                    dirty = false
                )
            )
        } finally {
            loading = false
        }
    }

    val activePage = normalizedPages.firstOrNull { it.id == activePageId } ?: normalizedPages.firstOrNull()
    val activePageStore = activePage?.id?.let(::pageStore)
    val currentState = if (pageScopedState) activePageStore?.workingState else workingState
    val activePageIndex = activePage?.let { normalizedPages.indexOfFirst { page -> page.id == it.id } }?.coerceAtLeast(0) ?: 0
    val activePageImageField = resolvePageImageField(activePageIndex)
    val activePageImage = if (pageScopedState) {
        activePage?.id?.let { activePageStore?.imageValue ?: imageDrafts[it] }.orEmpty().ifBlank { imageFallbackSrc }
    } else {
        activePage?.id?.let { imageDrafts[it] }.orEmpty().ifBlank { imageFallbackSrc }
    }
    val activeStickyFields = remember(activePage?.id, normalizedAnchorFields, normalizedPageAnchorFields) {
        normalizeFields(
            if (pageScopedState) normalizedPageAnchorFields[activePage?.id].orEmpty()
            else normalizedAnchorFields
        )
    }
    val activeDataFields = activePage?.fields.orEmpty()
    val activeRefreshFields = remember(activePage?.id, activeStickyFields, activeDataFields) {
        (activeStickyFields + activeDataFields).distinctBy { it.id }
    }
    val activeRefreshPages = remember(activePage?.id, normalizedPages) {
        activePage?.let { listOf(it) } ?: emptyList()
    }

    fun applyNewRecord(newState: All) {
        committedState = null
        workingState = newState
        pageStores = emptyMap()
        selectedEntityId = newState.id
        resetFieldState(newState)
        mergeAnchorFieldState(anchorCommittedState)
        activePageId = createPageId.ifBlank { normalizedPages.firstOrNull()?.id.orEmpty() }
        isDirty = true
    }

    fun createNewRecord() {
        if (!canCreate) return
        if (createDialog != null) {
            createDialogOpen = true
            return
        }
        applyNewRecord(createState?.invoke() ?: createNewState())
    }

    fun saveCurrentState(pageAction: EngineKarteiPageAction? = null) {
        val refreshFields = activeRefreshFields
        val refreshPages = activeRefreshPages

        saving = true
        scope.launch {
            try {
                if (pageScopedState && activePage != null) {
                    val pageId = activePage.id
                    val currentStore = pageStore(pageId)
                    val currentPageState = currentStore.workingState ?: return@launch
                    val drafted = rebuildPageWorkingState(pageId, currentStore.committedState ?: currentPageState, currentStore).copy(aktiv = false)
                    val prepared = drafted.copy(aktiv = true)
                    if (screenKey == "MeinProfilScreen" && pageId == "page_3") {
                        debugMeinProfilNetzwerk(
                            "save-current-state",
                            mapOf(
                                "selectedEntityId" to selectedEntityId,
                                "pageId" to pageId,
                                "readonlyFields" to activeRefreshFields.filter { !it.aktiv }.map { it.id },
                                "checkboxStore" to currentStore.checkValues.mapValues { it.value.toString() },
                                "prepared" to mapOf(
                                    "terminkalenderOnline" to prepared.terminkalenderOnline.toString(),
                                    "terminkalenderIntern" to prepared.terminkalenderIntern.toString(),
                                    "profilOnline" to prepared.profilOnline.toString(),
                                    "profilIntern" to prepared.profilIntern.toString()
                                )
                            )
                        )
                    }
                    val saved = normalizeState(
                        savePageState?.invoke(selectedEntityId, pageId, prepared)
                            ?: if (pageAction != null) pageAction.action(prepared) else saveState(prepared),
                        prepared.id
                    )
                    if (!saved.aktiv) {
                        applyPageStore(pageId, currentStore.copy(workingState = drafted, dirty = true))
                        return@launch
                    }
                    val extracted = extractFieldState(saved, activeRefreshFields)
                    applyPageStore(
                        pageId,
                        currentStore.copy(
                            committedState = saved,
                            workingState = saved,
                            textValues = extracted.textValues,
                            checkValues = extracted.checkValues,
                            imageValue = saved.toJsonObject().readString(activePageImageField).ifBlank { currentStore.imageValue.ifBlank { imageFallbackSrc } },
                            dirty = false
                        )
                    )
                    return@launch
                }
                val current = workingState ?: return@launch
                val drafted = rebuildWorkingState(committedState ?: current).copy(aktiv = false)
                val isDeckblattPage = anchorDeckblatt && activePageIndex == 0
                val prepared = drafted.copy(aktiv = true)
                if (isDeckblattPage) {
                    val anchorSaved = saveAnchorState?.invoke(prepared.copy(id = KARTEI_DECKBLATT_ID))
                        ?.let { normalizeState(it, KARTEI_DECKBLATT_ID) }
                        ?: normalizeState(prepared.copy(id = KARTEI_DECKBLATT_ID), KARTEI_DECKBLATT_ID)
                    if (!anchorSaved.aktiv) {
                        workingState = drafted
                        isDirty = true
                        return@launch
                    }
                    anchorCommittedState = anchorSaved
                    mergeAnchorFieldState(anchorSaved)
                    isDirty = false
                    return@launch
                }
                val saved = normalizeState(
                    if (pageAction != null) pageAction.action(prepared) else saveState(prepared),
                    prepared.id
                )

                if (!saved.aktiv) {
                    workingState = drafted
                    isDirty = true
                    return@launch
                }

                committedState = saved
                workingState = saved
                selectedEntityId = saved.id
                resetFieldState(
                    state = saved,
                    fields = refreshFields,
                    pages = refreshPages,
                    replaceAll = false
                )
                isDirty = false
            } finally {
                saving = false
            }
        }
    }

    val activeSecondaryAction = secondaryPageActions[activePageIndex]
    val activeIsDirty = if (pageScopedState) activePage?.id?.let { pageStores[it]?.dirty } == true else isDirty
    val forceProfileSaveVisible = screenKey == "MeinProfilScreen"

    EngineKarteiRootLayout(title = screenTitle) {
        EngineKarteiHeaderBar(
            entities = entities,
            selectedEntityId = selectedEntityId,
            onEntityChange = { newId ->
                if (newId.isNotBlank() && newId != selectedEntityId && !loading && !saving) {
                    selectedEntityId = newId
                }
            },
            onSave = { saveCurrentState(pageActions[activePageIndex]) },
            secondaryAction = activeSecondaryAction,
            onSecondaryAction = { saveCurrentState(activeSecondaryAction) },
            showSave = showSave && !imageEditorVisible,
            saveEnabled = !loading && !saving && (activeIsDirty || forceProfileSaveVisible),
            saving = saving,
            onCreateNew = { createNewRecord() },
            entityId = entityId,
            entityLabel = entityLabel,
            selectorWidthPx = selectorWidthPx,
            showCreate = showCreate,
            selectorContent = selectorContent,
            actionContent = actionContent,
            activePageId = activePage?.id.orEmpty(),
            centerContent = {
                if (activePage != null) {
                    val deckPages = normalizedPages.mapIndexed { index, page ->
                        All(id = page.id, bezeichnung = page.pageLabel, art = page.kind, aktiv = index == 0)
                    }
                    EngineKarteiPageNav(
                        pages = deckPages,
                        activePageId = activePage.id,
                        onPageChange = { activePageId = it },
                        screenKey = screenKey
                    )
                }
            }
        )

        if (createDialogOpen && createDialog != null) {
            createDialog(
                { state ->
                    createDialogOpen = false
                    applyNewRecord(state)
                },
                {
                    createDialogOpen = false
                    applyNewRecord(createState?.invoke() ?: createNewState())
                },
                { createDialogOpen = false }
            )
        }

        if (activePage != null && currentState != null) {
            val isDeckblattActive = anchorDeckblatt && activePageIndex == 0
            val stickyLabels = if (isDeckblattActive) emptyList() else activeStickyFields
            val pageLabels = normalizeFields(activePage.fields)
            val fieldGroups = groupFields(pageLabels, visibleAreas)
            val resolvedImageBlock: @Composable () -> Unit = if (staticPhotoSrc != null) {
                {
                    Div(attrs = {
                        style {
                            width(100.percent)
                            height(100.percent)
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            alignItems(AlignItems.Center)
                            property("overflow", "hidden")
                        }
                    }) {
                        Img(src = staticPhotoSrc, attrs = {
                            style {
                                property("display", "block")
                                property("max-width", "100%")
                                property("max-height", "100%")
                                property("width", "auto")
                                property("height", "auto")
                                property("object-fit", "contain")
                                property("object-position", "center center")
                                margin(0.px)
                            }
                        })
                    }
                }
            } else {
                {
                    EngineKarteiImageView(
                        imageSrc = activePageImage,
                        fallbackSrc = imageFallbackSrc,
                        editing = true,
                        disabled = loading || saving,
                        statusLabel = "",
                        onStatusChange = {},
                        onImageChange = { newImage -> setImageValue(activePage.id, activePageImageField, newImage) },
                        onEditorVisibilityChange = { imageEditorVisible = it },
                        onLoadGalleryImages = {
                            val targetId = currentState.id
                            if (targetId.isBlank() || targetId == "neu") emptyList() else loadGalleryImages(targetId)
                        },
                        onUploadGalleryImage = { file ->
                            val targetId = currentState.id
                            if (targetId.isBlank() || targetId == "neu") null
                            else {
                                val uploaded = uploadGalleryImage(targetId, file)
                                val uploadedJson = uploaded?.toJsonObject()
                                val newImage = uploadedJson?.readString("image")
                                    ?.ifBlank { uploadedJson.readString("src") }
                                    ?.ifBlank { uploadedJson.readString("url") }
                                    ?.ifBlank { null }
                                if (!newImage.isNullOrBlank()) setImageValue(activePage.id, activePageImageField, newImage)
                                uploaded
                            }
                        },
                        onDeleteGalleryImage = { image ->
                            val targetId = currentState.id
                            if (targetId.isBlank() || targetId == "neu") false
                            else deleteGalleryImage(targetId, image)
                        }
                    )
                }
            }

            EngineKarteiDeck(
                pages = normalizedPages.mapIndexed { index, page ->
                    All(id = page.id, bezeichnung = page.pageLabel, art = page.kind, aktiv = index == 0)
                },
                activePageId = activePage.id,
                onPageChange = { activePageId = it },
                screenKey = screenKey,
            ) {
                EngineKarteiPageContent(
                    page = All(id = activePage.id, bezeichnung = activePage.pageLabel, art = activePage.kind, aktiv = true),
                    stickyFields = stickyLabels,
                    fieldGroups = fieldGroups,
                    metaGroup = null,
                    showPhoto = showPhoto,
                    imageBlock = resolvedImageBlock,
                    pageOverrideContent = pageOverrides[activePage.id]?.let { override ->
                        {
                            override.compose(
                                (if (pageScopedState) activePageStore?.textValues.orEmpty() else textValues) + mapOf(
                                    "id" to currentState.id,
                                    "pageId" to activePage.id
                                ),
                                { fieldId, value -> setTextValue(fieldId, value) },
                                { values -> setMultipleTextValues(values) }
                            )
                        }
                    },
                    fieldRenderer = { field, readonly ->
                        val fieldId = field.id
                        if (fieldId.isBlank()) return@EngineKarteiPageContent

                        val override = fieldOverrides[fieldId]
                        if (override != null) {
                            override.compose(
                                (if (pageScopedState) activePageStore?.textValues?.get(fieldId) else textValues[fieldId]).orEmpty(),
                                { v -> setTextValue(fieldId, v) },
                                { m -> setMultipleTextValues(m) }
                            )
                        } else {
                            val labelText = field.header
                            val options = field.options
                            val art = field.type.lowercase()
                            val editable = field.editable && !readonly

                            when {
                                !editable -> {
                                    EngineKarteiInlineInput(
                                        label = labelText,
                                        value = (if (pageScopedState) activePageStore?.textValues?.get(fieldId) else textValues[fieldId]).orEmpty(),
                                        editing = false,
                                        placeholderText = ""
                                    ) { }
                                }

                                art == EngineKarteiFieldType.CHECKBOX -> {
                                    EngineKarteiCheckbox(
                                        label = labelText,
                                        checked = if (pageScopedState) activePageStore?.checkValues?.get(fieldId) == true else checkValues[fieldId] == true,
                                        editing = true,
                                        onChange = { checked -> setCheckValue(fieldId, checked) }
                                    )
                                }

                                options.isNotEmpty() || art == EngineKarteiFieldType.DROPDOWN || art == "select" -> {
                                    EngineKarteiDropdown(
                                        label = labelText,
                                        value = (if (pageScopedState) activePageStore?.textValues?.get(fieldId) else textValues[fieldId]).orEmpty(),
                                        options = options,
                                        onChange = { value -> setTextValue(fieldId, value) }
                                    )
                                }

                                else -> {
                                    EngineKarteiInlineInput(
                                        label = labelText,
                                        value = (if (pageScopedState) activePageStore?.textValues?.get(fieldId) else textValues[fieldId]).orEmpty(),
                                        editing = true,
                                        placeholderText = field.placeholder
                                    ) { value ->
                                        setTextValue(fieldId, value)
                                    }
                                }
                            }
                        } // else (no fieldOverride)
                    }
                )
            }
        } else {
            Div { }
        }
    }
}



